import { ReactNode, Suspense, useState } from "react";

import { connect, ConnectedProps } from "react-redux";

import { Outlet, useLocation } from "react-router";

import { AltHeader } from "../../Components/Static/AltHeader";

import { NavigationModule } from "../../Components/Common/NavigationModule";

import AlertManager from "../../Components/Common/Firestore/Alerts/AlertManager";

import { MessageBanner } from "../../Components/Common/Firestore/MessageBanner";

import { MessageModal } from "../../Components/Common/Firestore/MessageModal";

import { MessageFullScreen } from "../../Components/Common/Firestore/MessageFullScreen";

import { TrialBanner } from "../../Components/Billing/TrialBanner";

import { OutreachProvider } from "../../Components/Outreach/OutreachProvider";
import { TutorialGlobalBanner } from "../../Components/Tutorial/TutorialGlobalBanner";

import { useClientMessages } from "../../Components/Common/Firestore/Client/ClientMessageContext";

import { ContentSkeleton } from "../../Components/Common/ContentSkeleton";

import { PATH } from "../../Constants/paths";

import { TrialBannerProvider, useTrialBanner } from "../../context/TrialBannerContext";

import { TutorialProvider } from "../../context/TutorialContext";

import { USER_ROLE } from "../../Types/user";



interface Props extends ConnectedProps<typeof connector> {

  hideNav?: Boolean;

  children?: ReactNode;

}



const mapStateToProps = (state: any) => ({

  user: state.auth.user,

  isAuthenticated: state.auth.isAuthenticated

});



const connector = connect(mapStateToProps);



// Routes where Firestore components should NOT be shown

const EXCLUDED_ROUTES = [

  PATH.LANDING,

  PATH.PRICING,

  PATH.SECURITY,

  PATH.HELP,

  PATH.CONTACT,

  PATH.DEMO,

  PATH.LOGIN,

  PATH.PRIVACY_POLICY,

  PATH.TERMS_OF_SERVICE,

];



const MARKETING_SHELL_ROUTES = [PATH.LANDING, PATH.PRICING, PATH.CONTACT, PATH.DEMO];

const NAV_COLLAPSED_STORAGE_KEY = "intentico.navigation.collapsed";



const MainLayoutShell = (props: Props) => {

  const { hideNav, children, user, isAuthenticated } = props;

  const { visibleMessages } = useClientMessages();

  const { isTrialing, isCollapsed } = useTrialBanner();

  const location = useLocation();

  const [isNavCollapsed, setIsNavCollapsed] = useState(() => {
    try {
      return window.localStorage.getItem(NAV_COLLAPSED_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggleNavigation = () => {
    setIsNavCollapsed((wasCollapsed) => {
      const nextValue = !wasCollapsed;

      try {
        window.localStorage.setItem(NAV_COLLAPSED_STORAGE_KEY, String(nextValue));
      } catch {
        // The layout should still work when browser storage is unavailable.
      }

      return nextValue;
    });
  };

  

  // Check if current route should show Firestore components

  const shouldShowFirestoreComponents = isAuthenticated && 

    user?.id && 

    !EXCLUDED_ROUTES.includes(location.pathname);

  

  const useMarketingShell = MARKETING_SHELL_ROUTES.includes(location.pathname);

  

  // Check if there are banner messages

  const hasBannerMessages = visibleMessages.some(message => 

    message?.data?.displayType === 'BANNER'

  );



  const hasExpandedTrialBanner = isTrialing && !isCollapsed;

  

  // Apply different CSS class based on banner presence

  const showNav = hideNav !== true;

  const containerClassName = [

    'mainLayout_container',

    hasBannerMessages || hasExpandedTrialBanner ? 'mainLayout_withBanner' : 'mainLayout_withoutBanner',

    showNav ? 'mainLayout_withNav' : 'mainLayout_withoutNav',

    showNav && isNavCollapsed ? 'mainLayout_navCollapsed' : '',

  ].filter(Boolean).join(' ');

  

  return (

    <div className={containerClassName}>

      {!useMarketingShell && <AltHeader />}



      {shouldShowFirestoreComponents && <TrialBanner />}



      {/* Message Banner - only show on protected routes */}

      {shouldShowFirestoreComponents && (

        <MessageBanner />

      )}

      

      <div className="mainLayout_body">

        {showNav && (

          <aside className="mainLayout_sidebar">

            <NavigationModule
              isCollapsed={isNavCollapsed}
              onToggleCollapsed={toggleNavigation}
            />

          </aside>

        )}



        <div className="mainLayout_wrapper">

          <div className="mainLayout_content">

            <TutorialGlobalBanner />

            <OutreachProvider>

              {children ?? (

                <Suspense fallback={<ContentSkeleton />}>

                  <Outlet />

                </Suspense>

              )}

            </OutreachProvider>

          </div>

        </div>

      </div>

      

      {/* Alert Manager - only show on protected routes */}

      {shouldShowFirestoreComponents && (

        <AlertManager />

      )}

      

      {/* Message Modal - only show on protected routes */}

      {shouldShowFirestoreComponents && (

        <MessageModal />

      )}

      

      {/* Message Full Screen - only show on protected routes */}

      {shouldShowFirestoreComponents && (

        <MessageFullScreen />

      )}

    </div>

  );

};



const _MainLayout = (props: Props) => {

  const { user, isAuthenticated, hideNav } = props;

  const location = useLocation();



  const shouldShowFirestoreComponents = isAuthenticated &&

    user?.id &&

    !EXCLUDED_ROUTES.includes(location.pathname);



  const showTrialBanner =

    shouldShowFirestoreComponents && user?.role === USER_ROLE.CLIENT_ADMIN;

  const showTutorial =

    shouldShowFirestoreComponents && hideNav !== true;



  return (

    <TrialBannerProvider enabled={showTrialBanner} clientId={user?.client_id}>

      <TutorialProvider

        enabled={showTutorial}

        userId={user?.id}

        userRole={user?.role}

      >

        <MainLayoutShell {...props} />

      </TutorialProvider>

    </TrialBannerProvider>

  );

};



export const MainLayout = connector(_MainLayout);


